/**
 * Hero Brand Utilities
 * Helper functions for managing Hero brand implementation
 */

import { getBusinessTypeTag } from './businessType';

// Domains that serve the Herotolk brand, matched exactly or as a subdomain.
// `herotolk.com` is registered for future use; `herotolk.net` is live.
const HEROTOLK_DOMAINS = ['herotolk.com', 'herotolk.net'];

// Herotolk environments served from under tulka.com: `herotolk-app.tulka.com` and
// its staging/release siblings, which all carry the `herotolk-` label prefix.
const TULKA_DOMAIN = 'tulka.com';
const HEROTOLK_LABEL_PREFIX = 'herotolk-';

// URL parameter that forces the branding on (or off) so it can be exercised without a
// Herotolk address. Sticky for the tab, because history.push() drops the query string
// as the user moves through a wizard.
export const HEROTOLK_BRANDING_PARAM = 'useHerotolkBranding';

// The override is honoured on localhost and on the preprod and staging apps only.
// isHeroBrandEnabled() gates business behaviour as well as visuals — contract
// filtering, translation access, required invoicing fields, the Intercom workspace —
// so a real user must not be able to change any of it from a query parameter. The list
// is default-deny: production, release and any unrecognised host ignore the parameter.
const PREVIEW_HOSTS = ['localhost', '127.0.0.1'];
const PREVIEW_ENV_LABELS = ['preprod', 'staging'];

/**
 * Check whether a hostname is a Herotolk address.
 * An explicit allowlist rather than a substring test — `hostname.includes('herotolk')`
 * would also accept lookalikes such as `evil-herotolk.com` and `herotolk.evil.com`.
 * @param {string} hostname
 * @returns {boolean}
 */
export const isHeroBrandHost = (hostname) => {
  const host = (hostname || '').toLowerCase();
  if (HEROTOLK_DOMAINS.some(domain => host === domain || host.endsWith(`.${domain}`))) {
    return true;
  }
  if (!host.endsWith(`.${TULKA_DOMAIN}`)) {
    return false;
  }
  return host.split('.')[0].startsWith(HEROTOLK_LABEL_PREFIX);
};

/**
 * Whether the `useHerotolkBranding` override may be used on this host.
 * @param {string} hostname
 * @returns {boolean}
 */
export const isHeroBrandPreviewHost = (hostname) => {
  const host = (hostname || '').toLowerCase();
  if (PREVIEW_HOSTS.includes(host)) {
    return true;
  }
  // `preprod-app.tulka.com`, `staging-app.tulka.com`, `herotolk-app-staging.tulka.com`.
  return host.split('.')[0].split('-').some(label => PREVIEW_ENV_LABELS.includes(label));
};

const readParam = (source, param) => {
  if (!source) {
    return null;
  }
  return new URLSearchParams(source.replace(/^[?#]/, '')).get(param);
};

// Only the two documented values mean anything. Anything else — `=1`, `=yes`, an empty
// value — is ignored rather than read as an opt-out.
const parseBrandingParam = (value) => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return null;
};

const readStickyBranding = () => {
  try {
    return window.sessionStorage.getItem(HEROTOLK_BRANDING_PARAM);
  } catch (error) {
    // Storage can be unavailable (private browsing, blocked cookies). Brand
    // detection runs app-wide, so it must never throw.
    return null;
  }
};

const persistStickyBranding = (value) => {
  try {
    window.sessionStorage.setItem(HEROTOLK_BRANDING_PARAM, value);
  } catch (error) {
    // Nothing to do — the parameter simply won't survive the next navigation.
  }
};

/**
 * Resolve the `useHerotolkBranding` override, if one is in effect.
 * Read from the query string and from the hash — the app already passes params via
 * the hash — then remembered for the rest of the tab session. Ignored entirely off a
 * preview host.
 * @returns {boolean|null} true / false when forced, null when not set
 */
const getForcedHeroBrand = () => {
  if (!isHeroBrandPreviewHost(window?.location?.hostname)) {
    return null;
  }

  const fromUrl = parseBrandingParam(readParam(window?.location?.search, HEROTOLK_BRANDING_PARAM))
    ?? parseBrandingParam(readParam(window?.location?.hash, HEROTOLK_BRANDING_PARAM));

  if (fromUrl !== null) {
    persistStickyBranding(fromUrl ? 'true' : 'false');
    return fromUrl;
  }

  const sticky = readStickyBranding();
  if (sticky === null) {
    return null;
  }
  return sticky === 'true';
};

/**
 * Check if Hero brand is enabled.
 * True when the app is served from a Herotolk address, or the logged-in user's business
 * type is a Herotolk one — or, on a preview host only, when the `useHerotolkBranding`
 * override says so.
 * @returns {boolean}
 */
export const isHeroBrandEnabled = () => {
  const forced = getForcedHeroBrand();
  if (forced !== null) {
    return forced;
  }

  if (isHeroBrandHost(window?.location?.hostname)) {
    return true;
  }

  return (getBusinessTypeTag() || '').includes('herotolk');
};

// Apply Hero brand class to body
export const applyHeroBrandStyles = () => {
  if (isHeroBrandEnabled()) {
    document.body.classList.add('hero-brand');
  } else {
    document.body.classList.remove('hero-brand');
  }
};

// Get Hero brand colors
export const getHeroBrandColors = () => {
  return {
    purple: '#8B1C93',
    beige: '#E2DBC8',
    darkBrown: '#531530',
    blue: '#0093B1',
    green: '#1DAF8E',
    coral: '#F08372',
    beigeBackground: 'rgba(226, 219, 200, 0.2)'
  };
};

// Get Hero brand fonts
export const getHeroBrandFonts = () => {
  return {
    primary: 'Arial, Helvetica, sans-serif',
    special: 'Bradley Hand ITC, cursive',
    fallback: 'Arial, sans-serif'
  };
};

// Get Hero brand taglines
export const getHeroTaglines = () => {
  return {
    norwegian: 'Gjør en ny framtid mulig',
    english: 'Enabling a new future',
    german: 'Eine neue Zukunft möglich machen'
  };
};

// Initialize Hero brand on app load
export const initializeHeroBrand = () => {
  // Apply styles
  applyHeroBrandStyles();

  // Set CSS custom properties if Hero brand is enabled
  if (isHeroBrandEnabled()) {
    const colors = getHeroBrandColors();
    const fonts = getHeroBrandFonts();

    const root = document.documentElement;

    // Set color variables
    root.style.setProperty('--hero-purple', colors.purple);
    root.style.setProperty('--hero-beige', colors.beige);
    root.style.setProperty('--hero-dark-brown', colors.darkBrown);
    root.style.setProperty('--hero-blue', colors.blue);
    root.style.setProperty('--hero-green', colors.green);
    root.style.setProperty('--hero-coral', colors.coral);
    root.style.setProperty('--hero-beige-background', colors.beigeBackground);

    // Set font variables
    root.style.setProperty('--hero-font-family', fonts.primary);
    root.style.setProperty('--hero-font-special', fonts.special);

    console.log('Hero brand initialized');
  }
};

// Component wrapper for Hero brand conditional rendering
export const withHeroBrand = (HeroComponent, FallbackComponent = null) => {
  return (props) => {
    if (isHeroBrandEnabled()) {
      return HeroComponent ? <HeroComponent {...props} /> : null;
    }
    return FallbackComponent ? <FallbackComponent {...props} /> : null;
  };
};

// Hero brand configuration object
export const heroBrandConfig = {
  enabled: isHeroBrandEnabled(),
  colors: getHeroBrandColors(),
  fonts: getHeroBrandFonts(),
  taglines: getHeroTaglines(),

  // Service area icons
  serviceAreas: [
    { name: 'mottak', label: 'Mottak' },
    { name: 'tolk', label: 'Tolk' },
    { name: 'kurs', label: 'Kurs' }
  ],

  // Company values
  values: [
    { name: 'likeverd', label: 'Likeverd' },
    { name: 'profesjonalitet', label: 'Profesjonalitet' },
    { name: 'apenhet', label: 'Åpenhet' }
  ],

  // Dialog bubble variants
  dialogBubbleVariants: ['normal', 'high', 'long'],

  // Graphic pattern variants (1-5 as per manual)
  graphicPatternVariants: [1, 2, 3, 4, 5]
};

export default heroBrandConfig;